"use client";

import { Box, styled, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LogoImage from "@/../public/img/poseidon/logo.png";
import HeroMobile from "@/../public/img/poseidon/hero.jpg";
import Image from "next/image";
import {
  AD_DETAILS_BORDER_RADIUS,
  BOX_SHADOW_SIZE_LARGE,
} from "@/app/layout/LayoutConstants";
import { TOP_NAV_HEIGHT_DESKTOP_NUMBER } from "@/app/layout/GlobalNav";
import { AD_DETAILS_DESKTOP_SPACING } from "../for-sale/details/[slug]/[id]/components/AdDetailsPageLayout";
import Link from "next/link";
import PillButton from "@/app/shared/PillButton";
import { RecaptchaProvider } from "@/app/layout/useRecaptcha";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { BuyerGuidesForm } from "./BuyerGuidesForm";
import { BuyerGuidesModal } from "./BuyerGuidesModal";

const POSEIDON_COLOR = "#47c0bb";

function FilledCheckIcon({
  inverted,
  fillColor,
}: {
  inverted?: boolean;
  fillColor?: string;
}) {
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: fillColor || (inverted ? POSEIDON_COLOR : "white"),
        color: inverted ? "white" : POSEIDON_COLOR,
        borderRadius: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0.25,
      }}
    >
      <CheckIcon
        sx={{
          fontSize: 14,
        }}
      />
    </Box>
  );
}

function FilledIcon({
  inverted,
  children,
}: {
  inverted?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: inverted ? POSEIDON_COLOR : "white",
        color: inverted ? "white" : POSEIDON_COLOR,
        borderRadius: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0.25,
      }}
    >
      {children}
    </Box>
  );
}

function ListItem({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        fontWeight: "bold",
      }}
      variant="body2"
    >
      <FilledCheckIcon /> {children}
    </Typography>
  );
}

function ItemCard({
  title,
  body,
  iconColor,
}: {
  title: string;
  body: string;
  light?: boolean;
  iconColor?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        backgroundColor: iconColor ? "grey.50" : "grey.200",
        padding: 2,
        borderRadius: 4,
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          display: "flex",
          gap: 2,
          alignItems: "center",
          fontSize: 14,
        }}
      >
        <FilledCheckIcon inverted fillColor={iconColor} /> {title}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>{body}</Typography>
    </Box>
  );
}

const Container = styled(Box)({
  maxWidth: "1200px",
  margin: "0 auto",
});

const steps = [
  {
    label: "Before Purchase",
    description: `Ask the right questions and organise the essentials before committing to your new horse.`,
  },
  {
    label: "Prepare for Travel",
    description:
      "Plan transport carefully and ensure everything is ready before moving day.",
  },
  {
    label: "Transition Gradually",
    description: `Incremental changes to feed and routine can help horses settle more comfortably.`,
  },
  {
    label: "Monitor & Support",
    description: `Give your new horse time, and keep an eye on them during the first few weeks as they adjust to their new environment.`,
  },
];

export function BuyerGuidesPage() {
  return (
    <Box
      sx={{
        backgroundColor: "white",
        height: "100%",
      }}
    >
      <Box
        component={Image}
        src={HeroMobile}
        alt=""
        sx={{
          width: "100%",
          height: "auto",
          display: { xs: "block", md: "none" },
        }}
      />
      <Box
        sx={(theme) => ({
          background: `url('/img/poseidon/hero.jpg') no-repeat center center`,
          backgroundSize: "cover",
          [theme.breakpoints.down("md")]: {
            background: `none`,
          },
        })}
      >
        <Container
          sx={(theme) => ({
            padding: theme.spacing(10, 4),
            color: "white",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "start",
            [theme.breakpoints.down("md")]: {
              color: "black",
              padding: 2,
              paddingBottom: 6,
            },
          })}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={(theme) => ({
              maxWidth: "700px",
              [theme.breakpoints.down("md")]: {
                fontSize: "2rem",
              },
            })}
          >
            Bringing Your New Horse Home Starts Here
          </Typography>
          <Typography sx={{ maxWidth: 500 }} variant="body2">
            Make the transition into horse ownership as smooth as possible with
            practical resources designed to simplify the process of purchasing
            and settling in your new horse. Our free guides are here to make the
            journey easier, more organised and less overwhelming.
          </Typography>

          <BuyerGuidesModal />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              paddingLeft: 2,
            }}
          >
            <ListItem>New Horse Checklist</ListItem>
            <ListItem>Vet Check Guide (PPE)</ListItem>
            <ListItem>Transportation Guide</ListItem>
            <ListItem>Diet Transition</ListItem>
          </Box>
        </Container>
      </Box>
      <Container
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "minmax(400px, 680px) 450px",
          justifyContent: "space-between",
          [theme.breakpoints.down("md")]: {
            gridTemplateColumns: "1fr",
          },
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: 4,
              background: "white",
              padding: 4,
              borderRadius: theme.spacing(
                0,
                0,
                AD_DETAILS_BORDER_RADIUS,
                AD_DETAILS_BORDER_RADIUS,
              ),
              [theme.breakpoints.down("sm")]: {
                padding: 2,
              },
            })}
          >
            <Link
              href="https://poseidonanimalhealth.co.nz/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image src={LogoImage} alt="Poseidon Animal Health" height={73} />
            </Link>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography variant="body2">
                Buying a new horse is exciting, but it also comes with a lot of
                moving parts. Changes in travel, routine, environment and diet
                can all place stress on horses during the transition period. And
                for owners it can be overwhelming and confusing, and sometimes
                it is just knowing what questions to ask and what what order
                things should be done.
              </Typography>
              <Typography variant="body2">
                To help support horse owners through the process, Horsezone has
                partnered with Poseidon Animal Health to provide a free
                collection of practical guides, checklists and downloadable
                resources designed to help simplify the journey.
              </Typography>
            </Box>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                maxWidth: 400,
              }}
            >
              A Simple Approach to Bringing Your Horse Home
            </Typography>
            <Box>
              {steps.map((step, index) => (
                <Box
                  key={step.label}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "25px 1fr",
                    columnGap: 2,
                  }}
                >
                  <Box
                    sx={
                      index === 0
                        ? {
                            border: "1px solid " + POSEIDON_COLOR,
                            width: "25px",
                            height: "25px",
                            borderRadius: "100%",
                            padding: "2px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            justifySelf: "center",
                          }
                        : {}
                    }
                  >
                    <Typography
                      sx={{
                        backgroundColor: POSEIDON_COLOR,
                        color: "white",
                        width: "20px",
                        height: "20px",
                        borderRadius: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        justifySelf: "center",
                      }}
                      variant="caption"
                    >
                      {index + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Box
                    sx={{
                      borderLeft: `1px solid ${POSEIDON_COLOR}`,
                      opacity: index === steps.length - 1 ? 0 : 1,
                      justifySelf: "center",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 430,
                      paddingTop: 1,
                      paddingBottom: 4,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="h5" component="h2">
              Poseidon’s Guides and Checklists
            </Typography>
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                [theme.breakpoints.down("lg")]: {
                  gridTemplateColumns: "1fr",
                },
              })}
            >
              <ItemCard
                title="New Horse Checklist"
                body="A checklist to help you stay organised before, during and after bringing your new horse home."
              />
              <ItemCard
                title="Vet Check Guide (PPE)"
                body="A simple guide to understanding pre-purchase vet checks and the key questions to ask before buying."
              />
              <ItemCard
                title="Transportation Guide"
                body="Keep important transport details, contacts and travel information organised for a smoother moving day."
              />
              <ItemCard
                title="Diet Transition Guide"
                body="Gradually transition your horse onto new feeds and routines with minimal digestive disruption"
              />
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  padding: 2,
                  borderRadius: 4,
                  backgroundColor: "grey.200",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <FilledIcon inverted>
                    <AddIcon sx={{ fontSize: 14 }} />
                  </FilledIcon>{" "}
                  Post-purchase guides
                </Typography>
                <Box
                  sx={(theme) => ({
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                    [theme.breakpoints.down("sm")]: {
                      gridTemplateColumns: "1fr",
                    },
                  })}
                >
                  <ItemCard
                    title="Travel Protocol"
                    body="The ultimate travel guide to help support your horse before, during and after transport."
                    iconColor="#c1902b"
                  />
                  <ItemCard
                    title="Ultimate Gut Reset Guide"
                    body="Start you ownership journey on the right hoof and ensure optimal digestive health with the Ultimate Gut Reset"
                    iconColor="#bb6fae"
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={(theme) => ({
                display: "flex",
                gap: 4,
                alignItems: "center",
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "column",
                  gap: 1,
                },
              })}
            >
              <BuyerGuidesModal />
              <Typography
                sx={{
                  fontSize: 10,
                  maxWidth: 250,
                  lineHeight: 1.2,
                  color: "grey.500",
                }}
              >
                Enter your details and we’ll send the free guides directly to
                your inbox as downloadable PDFs that you can save, print or fill
                out digitally.
              </Typography>
            </Box>
            <Box
              sx={(theme) => ({
                display: "flex",
                gap: 4,
                alignItems: "center",
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "column",
                },
              })}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h5" component="h2">
                  Backed by Equine Experts
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  These resources are built around practical, evidence-based
                  equine care. Working alongside leading veterinarians and
                  equine nutritionists. Poseidon Animal Health is a global
                  leader in their commitment to advancing equine gut health,
                  education and horse wellbeing.
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  Watch our Free webinar on the process of purchasing a new
                  horse and welcoming them the their new home.
                </Typography>
              </Box>
            </Box>

            <Link
              href="https://webinar.poseidonanimalhealth.com.au/new-horse"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PillButton
                primary
                sx={(theme) => ({
                  "&&&": {
                    backgroundColor: "#00003c",
                    fontSize: 16,
                    height: "45px",
                    alignSelf: "start",
                    padding: theme.spacing(2, 8),
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    ...theme.typography.body1,
                  },
                })}
              >
                Learn More <OpenInNewIcon sx={{ fontSize: 19 }} />
              </PillButton>
            </Link>
          </Box>
        </Box>
        <Box>
          <Box
            sx={(theme) => ({
              position: "sticky",
              top:
                TOP_NAV_HEIGHT_DESKTOP_NUMBER +
                Number(theme.spacing(4).replace("px", "")),
              margin: AD_DETAILS_DESKTOP_SPACING,
              marginLeft: 0,
              marginBottom: 0,
              paddingBottom: 4,
              [theme.breakpoints.down("lg")]: {
                maxWidth: "450px",
                margin: "0 auto",
                padding: 1,
              },
              display: "flex",
              flexDirection: "column",
              gap: 4,
            })}
          >
            <Box
              sx={(theme) => ({
                background: "white",
                borderRadius: AD_DETAILS_BORDER_RADIUS,
                padding: 4,
                boxShadow: BOX_SHADOW_SIZE_LARGE,
                [theme.breakpoints.down("md")]: {
                  padding: 2,
                },
              })}
            >
              <RecaptchaProvider>
                <BuyerGuidesForm position="page" />
              </RecaptchaProvider>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
